import { HttpClient } from './HttpClient';
import type { ApiClientConfig } from '../../types/api/common';

/**
 * Service registration interface
 */
interface ServiceRegistration<T> {
  factory: () => T;
  singleton: boolean;
  instance?: T;
  dependencies?: string[];
}

/**
 * Service factory interface
 */
export interface IServiceFactory {
  register<T>(name: string, factory: () => T, options?: ServiceOptions): void;
  resolve<T>(name: string): T;
  isRegistered(name: string): boolean;
  clear(): void;
}

/**
 * Service registration options
 */
interface ServiceOptions {
  singleton?: boolean;
  dependencies?: string[];
}

/**
 * API Service Factory with Dependency Injection
 * 
 * Features:
 * - Service registration and resolution
 * - Singleton pattern support
 * - Dependency injection
 * - Lazy loading
 * - Service lifecycle management
 * - Mock service support for testing
 * - Service health checking
 */
export class ApiServiceFactory implements IServiceFactory {
  private static instance: ApiServiceFactory;
  private services: Map<string, ServiceRegistration<any>> = new Map();
  private resolving: Set<string> = new Set(); // Circular dependency detection
  private httpClient: HttpClient;
  private mockMode: boolean = false;

  private constructor() {
    this.httpClient = HttpClient.getInstance();
    this.registerCoreServices();
  }

  /**
   * Get singleton instance of the service factory
   */
  public static getInstance(): ApiServiceFactory {
    if (!ApiServiceFactory.instance) {
      ApiServiceFactory.instance = new ApiServiceFactory();
    }
    return ApiServiceFactory.instance;
  }

  /**
   * Register core services that are always available
   */
  private registerCoreServices(): void {
    // Register HTTP client as a service
    this.register('HttpClient', () => this.httpClient, { singleton: true });
    
    // Register service factory itself for services that need it
    this.register('ServiceFactory', () => this, { singleton: true });
  }

  /**
   * Register a service with the factory
   */
  public register<T>(
    name: string, 
    factory: () => T, 
    options: ServiceOptions = {}
  ): void {
    const { singleton = true, dependencies = [] } = options;

    this.services.set(name, {
      factory,
      singleton,
      dependencies,
    });
  }

  /**
   * Resolve a service by name
   */
  public resolve<T>(name: string): T {
    const registration = this.services.get(name);
    
    if (!registration) {
      throw new Error(`Service '${name}' is not registered`);
    }

    // Check for circular dependencies
    if (this.resolving.has(name)) {
      const resolvingArray = Array.from(this.resolving);
      throw new Error(`Circular dependency detected: ${resolvingArray.join(' -> ')} -> ${name}`);
    }

    // Return existing singleton instance if available
    if (registration.singleton && registration.instance) {
      return registration.instance;
    }

    try {
      this.resolving.add(name);

      // Resolve dependencies first
      const dependencies = this.resolveDependencies(registration.dependencies || []);

      // Create service instance
      const instance = registration.factory();

      // Inject dependencies if the service supports it
      this.injectDependencies(instance, dependencies);

      // Store singleton instance
      if (registration.singleton) {
        registration.instance = instance;
      }

      this.resolving.delete(name);
      return instance;
    } catch (error) {
      this.resolving.delete(name);
      throw new Error(`Failed to resolve service '${name}': ${error}`);
    }
  }

  /**
   * Resolve multiple dependencies
   */
  private resolveDependencies(dependencies: string[]): Record<string, any> {
    const resolvedDependencies: Record<string, any> = {};

    for (const dependency of dependencies) {
      resolvedDependencies[dependency] = this.resolve(dependency);
    }

    return resolvedDependencies;
  }

  /**
   * Inject dependencies into service instance
   */
  private injectDependencies(instance: any, dependencies: Record<string, any>): void {
    // Check if service has a setDependencies method
    if (typeof instance.setDependencies === 'function') {
      instance.setDependencies(dependencies);
    }

    // Check if service has individual dependency setters
    for (const [name, dependency] of Object.entries(dependencies)) {
      const setterName = `set${name.charAt(0).toUpperCase() + name.slice(1)}`;
      if (typeof instance[setterName] === 'function') {
        instance[setterName](dependency);
      }
    }
  }

  /**
   * Check if a service is registered
   */
  public isRegistered(name: string): boolean {
    return this.services.has(name);
  }

  /**
   * Get all registered service names
   */
  public getRegisteredServices(): string[] {
    return Array.from(this.services.keys());
  }

  /**
   * Clear all services (useful for testing)
   */
  public clear(): void {
    // Clear instances but keep registrations
    for (const registration of this.services.values()) {
      delete registration.instance;
    }
  }

  /**
   * Clear all services and registrations
   */
  public reset(): void {
    this.services.clear();
    this.resolving.clear();
    this.registerCoreServices();
  }

  /**
   * Enable mock mode for testing
   */
  public enableMockMode(): void {
    this.mockMode = true;
  }

  /**
   * Disable mock mode
   */
  public disableMockMode(): void {
    this.mockMode = false;
  }

  /**
   * Check if mock mode is enabled
   */
  public isMockMode(): boolean {
    return this.mockMode;
  }

  /**
   * Register mock service (only works in mock mode)
   */
  public registerMock<T>(name: string, mockFactory: () => T): void {
    if (!this.mockMode) {
      return;
    }

    const originalName = `${name}_original`;
    
    // Backup original service if it exists
    if (this.services.has(name)) {
      const original = this.services.get(name)!;
      this.services.set(originalName, original);
    }

    // Register mock service
    this.register(name, mockFactory, { singleton: true });
  }

  /**
   * Restore original service (remove mock)
   */
  public restoreOriginal(name: string): void {
    const originalName = `${name}_original`;
    
    if (this.services.has(originalName)) {
      const original = this.services.get(originalName)!;
      this.services.set(name, original);
      this.services.delete(originalName);
    }
  }

  /**
   * Get service health status
   */
  public getServiceHealth(name: string): ServiceHealthStatus {
    const registration = this.services.get(name);
    
    if (!registration) {
      return {
        name,
        status: 'not_registered',
        message: 'Service is not registered',
      };
    }

    try {
      const instance = this.resolve<any>(name);
      
      // Check if service has health check method
      if (typeof instance.healthCheck === 'function') {
        const healthResult = instance.healthCheck();
        return {
          name,
          status: healthResult.healthy ? 'healthy' : 'unhealthy',
          message: healthResult.message || 'Service health check completed',
          details: healthResult.details,
        };
      }

      return {
        name,
        status: 'healthy',
        message: 'Service is available (no health check method)',
      };
    } catch (error) {
      return {
        name,
        status: 'unhealthy',
        message: `Service resolution failed: ${error}`,
      };
    }
  }

  /**
   * Get health status of all services
   */
  public getAllServicesHealth(): ServiceHealthStatus[] {
    return this.getRegisteredServices().map(name => this.getServiceHealth(name));
  }

  /**
   * Update HTTP client configuration
   */
  public updateHttpClientConfig(config: Partial<ApiClientConfig>): void {
    this.httpClient.updateConfig(config);
  }

  /**
   * Create a scoped factory for specific feature/module
   */
  public createScope(scopeName: string): ScopedServiceFactory {
    return new ScopedServiceFactory(this, scopeName);
  }

  /**
   * Dispose of a service instance (useful for cleanup)
   */
  public dispose(name: string): void {
    const registration = this.services.get(name);
    
    if (registration && registration.instance) {
      // Call dispose method if available
      if (typeof registration.instance.dispose === 'function') {
        registration.instance.dispose();
      }
      
      // Remove instance reference
      delete registration.instance;
    }
  }

  /**
   * Dispose of all service instances
   */
  public disposeAll(): void {
    for (const name of this.services.keys()) {
      this.dispose(name);
    }
  }
}

/**
 * Service health status interface
 */
interface ServiceHealthStatus {
  name: string;
  status: 'healthy' | 'unhealthy' | 'not_registered';
  message: string;
  details?: Record<string, any>;
}

/**
 * Scoped service factory for feature-specific services
 */
class ScopedServiceFactory implements IServiceFactory {
  private scope: string;
  private parentFactory: ApiServiceFactory;

  constructor(parentFactory: ApiServiceFactory, scope: string) {
    this.parentFactory = parentFactory;
    this.scope = scope;
  }

  /**
   * Register service with scope prefix
   */
  public register<T>(name: string, factory: () => T, options?: ServiceOptions): void {
    const scopedName = `${this.scope}.${name}`;
    this.parentFactory.register(scopedName, factory, options);
  }

  /**
   * Resolve service with scope prefix
   */
  public resolve<T>(name: string): T {
    const scopedName = `${this.scope}.${name}`;
    
    // Try scoped name first, fallback to global name
    if (this.parentFactory.isRegistered(scopedName)) {
      return this.parentFactory.resolve<T>(scopedName);
    } else if (this.parentFactory.isRegistered(name)) {
      return this.parentFactory.resolve<T>(name);
    } else {
      throw new Error(`Service '${name}' not found in scope '${this.scope}' or globally`);
    }
  }

  /**
   * Check if service is registered (checks both scoped and global)
   */
  public isRegistered(name: string): boolean {
    const scopedName = `${this.scope}.${name}`;
    return this.parentFactory.isRegistered(scopedName) || this.parentFactory.isRegistered(name);
  }

  /**
   * Clear scoped services only
   */
  public clear(): void {
    const services = this.parentFactory.getRegisteredServices();
    const scopedServices = services.filter(name => name.startsWith(`${this.scope}.`));
    
    for (const serviceName of scopedServices) {
      this.parentFactory.dispose(serviceName);
    }
  }
}

/**
 * Service factory instance for global use
 */
export const serviceFactory = ApiServiceFactory.getInstance();
